import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';

const crumbs = [
  { label: 'Home', link: '/' },
  { label: 'Products' },
];

const productsData = {
  tankers: {
    title: 'Oil Tankers & Chemical Tankers',
    description: 'High-integrity double-hull vessels engineered for secure liquid petroleum cargo and chemical transit under CCS/IMO standards.',
    items: [
      { name: '42000DWT Tanker', img: '42000dwt.jpg', specs: { loa: '185.00 m', beam: '32.20 m', draft: '11.50 m', engine: 'MAN B&W 6S50MC-C' } },
      { name: '38000DWT Product Tanker', img: '38000dwt.jpg', specs: { loa: '182.50 m', beam: '30.00 m', draft: '10.50 m', engine: 'Wärtsilä 6RT-flex50' } },
      { name: '35000DWT Product Tanker', img: '35000dwt.jpg', specs: { loa: '177.80 m', beam: '28.40 m', draft: '10.20 m', engine: 'MAN B&W 5S50ME-B' } }
    ]
  },
  carriers: {
    title: 'Bulk Carriers',
    description: 'Rugged layouts optimized for high-density dry-bulk cargo operations, featuring advanced tank-top load distribution configurations.',
    items: [
      { name: '92500DWT Bulk Carrier', img: '92500DWT.jpg', specs: { loa: '229.20 m', beam: '38.00 m', draft: '14.90 m', engine: 'MAN B&W 6S60MC-C' } },
      { name: '82000DWT Bulk Carrier', img: '82000DWT.jpg', specs: { loa: '229.00 m', beam: '32.26 m', draft: '14.45 m', engine: 'MAN B&W 6S60ME-C (CSR)' } },
      { name: '76000DWT Bulk Carrier', img: '76000DWT.jpg', specs: { loa: '225.00 m', beam: '32.20 m', draft: '13.80 m', engine: 'Wärtsilä 6RTA52U' } }
    ]
  },
  mpps: {
    title: 'Multi-Purpose Ships (MPP)',
    description: 'Versatile multi-purpose fleets outfitted with heavy-lift twin deck cranes to support complex industrial project cargo layouts.',
    items: [
      { name: '32000T MPP Container Vessel', img: '32000T-MPP-Container-Vessel.jpg', specs: { loa: '179.90 m', beam: '28.00 m', draft: '10.50 m', engine: 'Cranes: 2 x 80T / Box Hold' } },
      { name: '27000T MPP Container Vessel', img: '27000T-MPP-Container-Vessel.jpg', specs: { loa: '169.00 m', beam: '27.20 m', draft: '9.80 m', engine: 'Cranes: 2 x 60T Swivels' } },
      { name: '18000T MPP Container Vessel', img: '26800T-MPP-Container-Vessel.jpg', specs: { loa: '148.00 m', beam: '23.40 m', draft: '8.50 m', engine: 'Tweendeck Removable Panels' } },
      { name: '12000T MPP Container Vessel', img: '25000T-MPP-Container-Vessel.jpg', specs: { loa: '132.00 m', beam: '21.00 m', draft: '7.60 m', engine: 'Lightweight Versatile Draft' } }
    ]
  },
  tugs: {
    title: 'Azimuth Stern Drive (ASD) Tugs',
    description: 'High bollard pull harbor and escort tugboats fitted with 360-degree omnidirectional azimuth thrusters.',
    items: [
      { name: 'ASD Tug 75T BP', img: '75.jpg', specs: { loa: '36.50 m', beam: '11.80 m', pull: '75 Metric Tons', propulsion: 'Twin Niigata Z-Peller' } },
      { name: 'ASD Tug 65T BP', img: '65.jpg', specs: { loa: '34.00 m', beam: '11.00 m', pull: '65 Metric Tons', propulsion: 'Rolls-Royce US Thrusters' } },
      { name: 'ASD Tug 60T BP', img: '60.jpg', specs: { loa: '32.00 m', beam: '10.50 m', pull: '60 Metric Tons', propulsion: 'Schottel SRP Systems' } },
      { name: 'ASD Tug 50T BP', img: '50.jpg', specs: { loa: '31.00 m', beam: '10.00 m', pull: '50 Metric Tons', propulsion: 'Yanmar Medium Speed Engines' } },
      { name: 'ASD Tug 40T BP', img: '40.jpg', specs: { loa: '29.80 m', beam: '9.50 m', pull: '40 Metric Tons', propulsion: 'Compact Agile Harbor Hull' } }
    ]
  },
  containers: {
    title: 'Container Ships',
    description: 'High-speed cellular container carriers optimized with custom hull profiles for fuel efficiency and rapid turnarounds.',
    items: [
      { name: '14000DWT General-Container Carrier', img: '4250.jpg', specs: { loa: '142.00 m', beam: '22.60 m', draft: '8.20 m', capacity: 'Approx. 950 TEU / Cells' } }
    ]
  }
};

export default function Products() {
  const [expandedItem, setExpandedItem] = useState(null);

  const toggleSpecs = (key) => {
    setExpandedItem(expandedItem === key ? null : key);
  };

  return (
    <>
      <Breadcrumb title="Vessel Portfolio" crumbs={crumbs} />

      {/* Intro Header */}
      <section className="section" style={{ paddingBottom: '0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 40px' }}>
            <div className="eyebrow centered"><i className="fas fa-ship"></i> Fleet Registry</div>
            <h2 className="section-title centered">Proven Foundations, Built To Deliver</h2>
            <p className="section-subtitle centered">
              Review our verified active designs and past completed yard deployments. Each category can be fully customized to meet required cargo rules and classification parameters.
            </p>
          </div>
        </div>
      </section>

      {/* Dynamic Loops for Sections */}
      {Object.entries(productsData).map(([sectionKey, category], index) => (
        <section key={sectionKey} className={`section ${index % 2 === 1 ? 'section-alt' : ''}`} style={{ padding: '60px 0' }}>
          <div className="container">
            <div style={{ marginBottom: '35px' }}>
              <h3 style={{ fontSize: '28px', color: 'var(--ink)', marginBottom: '8px', fontWeight: '700' }}>
                {category.title}
              </h3>
              <p style={{ color: 'var(--ink-muted)', margin: 0, maxWidth: '650px' }}>{category.description}</p>
            </div>

            <div className="cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '30px' }}>
              {category.items.map((item, idx) => {
                const globalKey = `${sectionKey}-${idx}`;
                const hasPull = !!item.specs.pull;

                return (
                  <div key={idx} className="service-card reveal" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    
                    {/* Image Block: Pointing directly to root of public folder */}
                    <div style={{ width: '100%', height: '210px', overflow: 'hidden', background: '#eaeaea' }}>
                      <img 
                        src={`${process.env.PUBLIC_URL || ''}/${item.img}`} 
                        alt={item.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.style.display = 'flex';
                          e.target.parentNode.innerHTML = `<div style="margin:auto;text-align:center;color:#999;font-size:13px;"><i class="fas fa-ship fa-2x" style="display:block;margin-bottom:8px;"></i>${item.img} Missing</div>`;
                        }}
                      />
                    </div>
                    
                    {/* Info Block */}
                    <div style={{ padding: '24px', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <h4 style={{ fontSize: '18px', margin: '0 0 12px 0', color: 'var(--ink)', lineHeight: '1.4' }}>{item.name}</h4>
                        
                        {/* Expandable Specifications Area */}
                        {expandedItem === globalKey && (
                          <div style={{ background: 'rgba(0,0,0,0.03)', padding: '12px', borderRadius: '6px', marginBottom: '15px', fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                              <span style={{ color: '#666' }}>Length (LOA):</span>
                              <strong style={{ color: '#222' }}>{item.specs.loa}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                              <span style={{ color: '#666' }}>{hasPull ? 'Beam Width:' : 'Beam (Breadth):'}</span>
                              <strong style={{ color: '#222' }}>{item.specs.beam}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                              <span style={{ color: '#666' }}>{hasPull ? 'Bollard Pull:' : 'Design Draft:'}</span>
                              <strong style={{ color: 'var(--primary)' }}>{hasPull ? item.specs.pull : item.specs.draft}</strong>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', padding: '5px 0 0' }}>
                              <span style={{ color: '#666', fontSize: '11px' }}>{hasPull ? 'Thruster Drive Configuration:' : 'Engineering / Machinery Config:'}</span>
                              <strong style={{ color: '#444', marginTop: '2px', fontSize: '12px' }}>{item.specs.engine || item.specs.propulsion || item.specs.capacity}</strong>
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px' }}>
                        <button 
                          onClick={() => toggleSpecs(globalKey)} 
                          style={{ background: 'none', border: 'none', padding: '0', color: 'var(--ink-muted)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <i className={`fas ${expandedItem === globalKey ? 'fa-compress-alt' : 'fa-sliders-h'}`}></i>
                          {expandedItem === globalKey ? 'Hide Specs' : 'View Specs'}
                        </button>
                        
                        <Link to="/contact" style={{ fontSize: '13px', fontWeight: '600', color: 'var(--primary)', textDecoration: 'none' }}>
                          Inquire <i className="fas fa-arrow-right" style={{ fontSize: '11px', marginLeft: '2px' }}></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Fleet Action CTA */}
      <section className="section section-dark">
        <div className="container">
          <div style={{ background: 'rgba(232,80,28,0.1)', border: '1px solid rgba(232,80,28,0.3)', borderRadius: '14px', padding: '44px 36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h3 style={{ color: '#fff', fontSize: '22px', marginBottom: '6px' }}>Need custom deadweight blueprints?</h3>
              <p style={{ margin: 0 }}>Connect with our engineering office to discuss custom structural modification rules.</p>
            </div>
            <Link to="/contact" className="btn btn-primary">Request Blueprint Specs <i className="fas fa-arrow-right"></i></Link>
          </div>
        </div>
      </section>
    </>
  );
}